import React, { Component } from 'react'
import styles from './BrandInfoForm.module.scss'
import {
  LabeledInput, Button, Tag, IconButton, LabeledTextarea,
  LabeledGalleryDropZone, RelatedMediaFormSection
} from 'components'
import { icons } from 'assets'
import { inject } from 'mobx-react'
import { BRAND_STORE, BrandStore, GENERAL_STORE, GeneralStore } from 'store'
import { Brand } from 'models'

interface Props {
  [BRAND_STORE]?: BrandStore;
  [GENERAL_STORE]?: GeneralStore;
  brand?: any;
}

interface State {
  isScrollOnTop: boolean;
  name: string;
  websiteUrl: string;
  tags: string[];
  info: string;
  relatedMedia: any[];
}

class BrandInfoForm extends Component<Props, State> {
  isSubmitWithEnterKeyAllowed: boolean;
  imagesRef: React.RefObject<{
    handleImages: () => Promise<any>;
    getImages: () => any[];
  }>;

  constructor (props: Props) {
    super(props)
    this.state = {
      isScrollOnTop: true,
      name: '',
      websiteUrl: '',
      tags: [],
      info: '',
      relatedMedia: []
    }

    this.imagesRef = React.createRef()
    this.isSubmitWithEnterKeyAllowed = true

    this.handleTagInput = this.handleTagInput.bind(this)
    this.handleOnScroll = this.handleOnScroll.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps (props) {
    this.feedBrandDetails(props.brand)
  }

  feedBrandDetails (brand) {
    if (!brand) return

    this.setState({
      name: brand.name || '',
      websiteUrl: brand.websiteUrl || '',
      tags: brand.tags ? brand.tags.split(',') : [],
      info: brand.info || '',
      relatedMedia: brand.relatedMedia || []
    })
  }

  handleTagInput (tag: string) {
    tag = tag.trim().toLocaleLowerCase()
    const { tags } = this.state

    if (tags.includes(tag)) return

    this.setState({ tags: [...tags, tag] })
  }

  handleOnScroll (e: React.UIEvent<HTMLFormElement>) {
    const { isScrollOnTop: wasScrollOnTop } = this.state
    const isScrollOnTop = e.currentTarget.scrollTop === 0
    if (wasScrollOnTop !== isScrollOnTop) this.setState({ isScrollOnTop })
  }

  handleSubmit () {
    if (!this.isSubmitWithEnterKeyAllowed) return

    const {
      [BRAND_STORE]: {
        createBrand,
        editBrand
      },
      [GENERAL_STORE]: { showError, setLoadingOn },
      brand
    } = this.props

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isScrollOnTop, ...stateBrandDetails } = this.state

    if (!stateBrandDetails.name) {
      showError('Please provide the NAME of the brand.')
      return
    }

    setLoadingOn()

    this.imagesRef.current.handleImages()
      .then(() => {
        const brandDetails = {
          ...stateBrandDetails,
          tags: stateBrandDetails.tags.join(),
          images: this.imagesRef.current.getImages()
        } as Brand

        brand
          ? editBrand({ id: brand.id, ...brandDetails })
          : createBrand(brandDetails)
      })
      .catch(err => showError(err.message))
  }

  render () {
    const {
      name,
      websiteUrl,
      info,
      tags,
      isScrollOnTop,
      relatedMedia
    } = this.state
    const {
      [GENERAL_STORE]: {
        push
      },
      brand
    } = this.props

    return (
      <div className={styles.container}>

        <h1 className={!isScrollOnTop ? styles.overEffect : ''}>
          {
            brand
              ? brand.name ? `Edit "${brand.name}" brand` : ''
              : 'Add a New Brand'
          }
        </h1>

        <form onScroll={this.handleOnScroll}>
          <div>
            <LabeledInput
              label='Brand Name'
              placeholder='Type the name of the brand'
              onChange={value => this.setState({ name: value })}
              value={name}
            />
            <LabeledInput
              label='Website URL'
              placeholder='URL'
              type='url'
              onChange={value => this.setState({ websiteUrl: value })}
              value={websiteUrl}
            />
            <LabeledInput
              label='Tags'
              placeholder='Type a tag for the brand, press enter to set a tag'
              keysToHandle={['Enter']}
              onKeyDown={this.handleTagInput}
              onFocus={() => { this.isSubmitWithEnterKeyAllowed = false }}
              onBlur={() => { this.isSubmitWithEnterKeyAllowed = true }}
            />
            <div className={styles.tagsContainer}>
              {tags.map((tag, index) =>
                <Tag key={index} tag={tag}>
                  <IconButton
                    icon={icons.closeX}
                    className={styles.tagIcon}
                    onClick={() => this.setState({
                      tags: tags.filter(t => t !== tag)
                    })}
                  />
                </Tag>
              )}
            </div>
          </div>

          <div>
            <LabeledTextarea
              label='Description'
              placeholder={'Enter a Description = 3000 characters max'}
              onChange={value => this.setState({ info: value })}
              value={info}
            />
          </div>

          <LabeledGalleryDropZone
            ref={this.imagesRef}
            initialImages={brand ? brand.images : []}
          />

          <RelatedMediaFormSection
            selectedProjectsList={relatedMedia}
            setSelectedProjectsList={projects => this.setState({ relatedMedia: projects })}
          />

          <div className={styles.buttonsContainer}>
            <Button
              type='cancel'
              onClick={() => push('/brands')}
            >
              Discard Changes
            </Button>
            <Button
              type='submit'
              className={styles.saveBttn}
              onClick={this.handleSubmit}
            >
              Save Changes
            </Button>
          </div>

        </form>

      </div>
    )
  }
}

export default inject(BRAND_STORE, GENERAL_STORE)(BrandInfoForm)
